/**
 * Output type selection (Post vs Video) for Step 2
 */

import { motion } from "framer-motion";
import { Layers, Video, AlertTriangle } from "lucide-react";

export function OutputTypeSelector({ 
  outputType, 
  onSelect, 
  inputType, 
  postsRemaining, 
  videosRemaining,
  paygPostsRemaining,
  paygVideosRemaining
}) {
  const canMakePost = postsRemaining > 0 || paygPostsRemaining > 0;
  const canMakeVideo = videosRemaining > 0 || paygVideosRemaining > 0;

  // For uploaded video input, only video output makes sense
  if (inputType === "video") {
    return (
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-3">
          <Video className="w-8 h-8 text-viraly-orange" />
          <div>
            <p className="font-medium">Video Output</p>
            <p className="text-sm text-viraly-muted">Your uploaded video will be enhanced with branding and captions</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Post Option */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => canMakePost && onSelect("post")}
        disabled={!canMakePost}
        data-testid="output-post"
        className={`p-6 rounded-xl border-2 text-left transition-all ${
          !canMakePost 
            ? "border-white/5 opacity-50 cursor-not-allowed" 
            : outputType === "post"
              ? "border-viraly-orange bg-viraly-orange/10"
              : "border-white/10 hover:border-white/20"
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <Layers className="w-8 h-8 text-viraly-orange mb-3" />
            <h3 className="font-heading text-xl font-bold mb-1">Picture Post</h3>
            <p className="text-sm text-viraly-muted">AI-generated image with caption</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-viraly-orange">{postsRemaining + paygPostsRemaining}</span>
            <p className="text-xs text-viraly-muted">remaining</p>
          </div>
        </div>
        {!canMakePost && (
          <div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-xs text-red-400">No posts remaining</span>
          </div>
        )}
      </motion.button>

      {/* Video Option */}
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => canMakeVideo && onSelect("video")}
        disabled={!canMakeVideo}
        data-testid="output-video"
        className={`p-6 rounded-xl border-2 text-left transition-all ${
          !canMakeVideo 
            ? "border-white/5 opacity-50 cursor-not-allowed" 
            : outputType === "video"
              ? "border-viraly-orange bg-viraly-orange/10"
              : "border-white/10 hover:border-white/20"
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <Video className="w-8 h-8 text-viraly-orange mb-3" />
            <h3 className="font-heading text-xl font-bold mb-1">Video</h3>
            <p className="text-sm text-viraly-muted">AI-generated video with voiceover</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-viraly-orange">{videosRemaining + paygVideosRemaining}</span>
            <p className="text-xs text-viraly-muted">remaining</p>
          </div>
        </div>
        {!canMakeVideo && (
          <div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-xs text-red-400">No videos remaining</span>
          </div>
        )}
      </motion.button>
    </div>
  );
}
